import React, { useEffect, useState } from 'react';
import { api } from './AdminApp';
import Modal from './Modal';
import Toggle from './Toggle';
import ImageField from './ImageField';
import Loader from '../Components/Loader/Loader';

const EMPTY = {
  slug: '',
  name: '',
  icon: '',
  description: '',
  route_path: '',
  display_order: 0,
  is_active: true,
};

// Empty page-content block (hero + SEO fields)
const EMPTY_PAGE = {
  schema_type: 'MedicalProcedure',
  page_title: '',
  meta_description: '',
  canonical_url: '',
  hero_title: '',
  hero_subtitle: '',
  hero_stat_value: '',
  hero_stat_label: '',
  sections: [],
};

const SECTION_TYPES = [
  ['text', 'Text block'],
  ['list', 'List'],
  ['grid', 'Grid cards'],
  ['steps', 'Numbered steps'],
  ['faq', 'FAQ'],
  ['seo', 'SEO text'],
];

export default function Services() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('basic'); // basic | page

  const load = async () => {
    setLoading(true);
    try {
      const d = await api('/admin/services');
      setList(d.services || []);
      setError('');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing({ ...EMPTY, ...EMPTY_PAGE, id: 0 });
    setTab('basic');
  };

  const startEdit = (s) => {
    setEditing({
      id: s.id,
      slug: s.slug || '',
      name: s.name || '',
      icon: s.icon || '',
      description: s.description || '',
      route_path: s.route_path || '',
      display_order: s.display_order ?? 0,
      is_active: Boolean(s.is_active),
      image_url: s.image_url || null,
      // page content
      schema_type: s.schema_type || 'MedicalProcedure',
      page_title: s.page_title || '',
      meta_description: s.meta_description || '',
      canonical_url: s.canonical_url || '',
      hero_title: s.hero_title || '',
      hero_subtitle: s.hero_subtitle || '',
      hero_stat_value: s.hero_stat_value || '',
      hero_stat_label: s.hero_stat_label || '',
      sections: Array.isArray(s.sections) ? s.sections : [],
    });
    setTab('basic');
  };

  const save = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('id', String(editing.id || 0));
    fd.append('slug', editing.slug);
    fd.append('name', editing.name);
    fd.append('icon', editing.icon || '');
    fd.append('description', editing.description || '');
    fd.append('route_path', editing.route_path || '');
    fd.append('display_order', String(editing.display_order ?? 0));
    fd.append('is_active', editing.is_active ? '1' : '');
    fd.append('schema_type', editing.schema_type || 'MedicalProcedure');
    fd.append('page_title', editing.page_title || '');
    fd.append('meta_description', editing.meta_description || '');
    fd.append('canonical_url', editing.canonical_url || '');
    fd.append('hero_title', editing.hero_title || '');
    fd.append('hero_subtitle', editing.hero_subtitle || '');
    fd.append('hero_stat_value', editing.hero_stat_value || '');
    fd.append('hero_stat_label', editing.hero_stat_label || '');
    fd.append('sections', JSON.stringify(editing.sections || []));
    if (file) fd.append('image', file);
    try {
      await api('/admin/services/save', { method: 'POST', body: fd });
      setEditing(null);
      setFile(null);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await api('/admin/services/delete', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggle = async (id) => {
    try {
      await api('/admin/services/toggle', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const set = (k) => (e) =>
    setEditing({
      ...editing,
      [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    });

  // ---- sections editor helpers ----
  const setSection = (i, patch) =>
    setEditing((prev) => {
      const sections = prev.sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
      return { ...prev, sections };
    });

  const addSection = (type) =>
    setEditing((prev) => {
      const base = { type, title: '' };
      if (type === 'text' || type === 'seo') base.text = '';
      if (type === 'list' || type === 'grid') base.items = [''];
      if (type === 'steps') base.steps = [{ title: '', text: '' }];
      if (type === 'faq') base.items = [{ q: '', a: '' }];
      return { ...prev, sections: [...prev.sections, base] };
    });

  const removeSection = (i) =>
    setEditing((prev) => ({ ...prev, sections: prev.sections.filter((_, idx) => idx !== i) }));

  const moveSection = (i, dir) =>
    setEditing((prev) => {
      const sections = [...prev.sections];
      const j = i + dir;
      if (j < 0 || j >= sections.length) return prev;
      [sections[i], sections[j]] = [sections[j], sections[i]];
      return { ...prev, sections };
    });

  const removeImage = async () => {
    await api('/admin/services/delete-image', {
      method: 'POST',
      body: JSON.stringify({ id: editing.id }),
    });
    setEditing((prev) => ({ ...prev, image_url: null }));
    load();
  };

  const close = () => {
    setEditing(null);
    setFile(null);
  };

  return (
    <div>
      <div className="admin-page-head">
        <h1 className="admin-page-title">Services</h1>
        <button className="admin-btn admin-btn-primary" onClick={startNew}>
          + Add service
        </button>
      </div>
      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      {editing && (
        <Modal
          title={editing.id ? `Edit service #${editing.id}` : 'New service'}
          onClose={close}
          wide
        >
          <div className="admin-tabs">
            <button
              type="button"
              className={`admin-tab ${tab === 'basic' ? 'active' : ''}`}
              onClick={() => setTab('basic')}
            >
              Basic details
            </button>
            <button
              type="button"
              className={`admin-tab ${tab === 'page' ? 'active' : ''}`}
              onClick={() => setTab('page')}
            >
              Detail page content
            </button>
          </div>

          <form className="admin-form" onSubmit={save}>
            {tab === 'basic' && (
              <div className="admin-form-grid">
                <label>
                  Name *
                  <input value={editing.name} onChange={set('name')} required />
                </label>
                <label>
                  Slug (blank = from name)
                  <input value={editing.slug} onChange={set('slug')} />
                </label>
                <label>
                  Icon (emoji)
                  <input value={editing.icon} onChange={set('icon')} maxLength={16} />
                </label>
                <label>
                  Route path (e.g. /services/ivf)
                  <input value={editing.route_path} onChange={set('route_path')} />
                </label>
                <label>
                  Display order
                  <input type="number" value={editing.display_order} onChange={set('display_order')} />
                </label>
                <div className="admin-form-full">
                  <ImageField
                    label="Image"
                    url={editing.image_url}
                    file={file}
                    onPick={(f) => setFile(f)}
                    onRemove={removeImage}
                    hint="Service image · JPEG, PNG, WebP · up to 10 MB"
                  />
                </div>
                <label style={{ gridColumn: '1 / -1' }}>
                  Short description (used on cards and menus)
                  <textarea rows={3} value={editing.description} onChange={set('description')} />
                </label>
                <div className="admin-form-checks" style={{ gridColumn: '1 / -1' }}>
                  <label>
                    <input type="checkbox" checked={editing.is_active} onChange={set('is_active')} />
                    Visible on site
                  </label>
                </div>
              </div>
            )}

            {tab === 'page' && (
              <div className="admin-form-grid">
                <label>
                  Hero title
                  <input value={editing.hero_title} onChange={set('hero_title')} />
                </label>
                <label>
                  Hero stat value (e.g. 100%+)
                  <input value={editing.hero_stat_value} onChange={set('hero_stat_value')} />
                </label>
                <label style={{ gridColumn: '1 / -1' }}>
                  Hero subtitle
                  <input value={editing.hero_subtitle} onChange={set('hero_subtitle')} />
                </label>
                <label>
                  Hero stat label (e.g. Success Rate)
                  <input value={editing.hero_stat_label} onChange={set('hero_stat_label')} />
                </label>
                <label>
                  Schema type
                  <input value={editing.schema_type} onChange={set('schema_type')} />
                </label>
                <label style={{ gridColumn: '1 / -1' }}>
                  Page title (browser tab / SEO)
                  <input value={editing.page_title} onChange={set('page_title')} />
                </label>
                <label style={{ gridColumn: '1 / -1' }}>
                  Meta description (SEO)
                  <textarea rows={2} value={editing.meta_description} onChange={set('meta_description')} />
                </label>
                <label style={{ gridColumn: '1 / -1' }}>
                  Canonical URL
                  <input value={editing.canonical_url} onChange={set('canonical_url')} />
                </label>

                {/* ---------- Sections editor ---------- */}
                <div className="admin-form-full" style={{ gridColumn: '1 / -1' }}>
                  <h4 style={{ margin: '4px 0 8px' }}>Page sections (rendered in order)</h4>
                  {editing.sections.map((sec, i) => (
                    <div className="admin-section-block" key={i}>
                      <div className="admin-section-head">
                        <span className="admin-section-type">
                          {sec.type === 'steps'
                            ? 'Numbered steps'
                            : sec.type === 'seo'
                              ? 'SEO text'
                              : sec.type === 'faq'
                                ? 'FAQ'
                                : sec.type === 'grid'
                                  ? 'Grid cards'
                                  : sec.type === 'list'
                                    ? 'List'
                                    : 'Text block'}
                        </span>
                        <span className="admin-section-tools">
                          <button type="button" title="Move up" onClick={() => moveSection(i, -1)}>↑</button>
                          <button type="button" title="Move down" onClick={() => moveSection(i, 1)}>↓</button>
                          <button type="button" className="danger" title="Remove section" onClick={() => removeSection(i)}>×</button>
                        </span>
                      </div>

                      <label>
                        Section heading
                        <input value={sec.title || ''} onChange={(e) => setSection(i, { title: e.target.value })} />
                      </label>

                      {(sec.type === 'text' || sec.type === 'seo') && (
                        <label>
                          Text (blank line = new paragraph)
                          <textarea
                            rows={4}
                            value={sec.text || ''}
                            onChange={(e) => setSection(i, { text: e.target.value })}
                          />
                        </label>
                      )}

                      {(sec.type === 'list' || sec.type === 'grid') && (
                        <div>
                          <span className="admin-field-label">Items</span>
                          {(sec.items || []).map((it, j) => (
                            <div className="admin-inline-row" key={j}>
                              <input
                                value={it}
                                onChange={(e) =>
                                  setSection(i, {
                                    items: sec.items.map((x, xi) => (xi === j ? e.target.value : x)),
                                  })
                                }
                              />
                              <button
                                type="button"
                                className="admin-btn admin-btn-sm"
                                onClick={() => setSection(i, { items: sec.items.filter((_, xi) => xi !== j) })}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="admin-btn admin-btn-sm"
                            onClick={() => setSection(i, { items: [...(sec.items || []), ''] })}
                          >
                            + Add item
                          </button>
                        </div>
                      )}

                      {sec.type === 'steps' && (
                        <div>
                          <span className="admin-field-label">Steps</span>
                          {(sec.steps || []).map((st, j) => (
                            <div className="admin-step-row" key={j}>
                              <input
                                placeholder="Step title"
                                value={st.title || ''}
                                onChange={(e) =>
                                  setSection(i, {
                                    steps: sec.steps.map((x, xi) =>
                                      xi === j ? { ...x, title: e.target.value } : x
                                    ),
                                  })
                                }
                              />
                              <input
                                placeholder="Step description"
                                value={st.text || ''}
                                onChange={(e) =>
                                  setSection(i, {
                                    steps: sec.steps.map((x, xi) =>
                                      xi === j ? { ...x, text: e.target.value } : x
                                    ),
                                  })
                                }
                              />
                              <button
                                type="button"
                                className="admin-btn admin-btn-sm"
                                onClick={() => setSection(i, { steps: sec.steps.filter((_, xi) => xi !== j) })}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="admin-btn admin-btn-sm"
                            onClick={() =>
                              setSection(i, { steps: [...(sec.steps || []), { title: '', text: '' }] })
                            }
                          >
                            + Add step
                          </button>
                        </div>
                      )}

                      {sec.type === 'faq' && (
                        <div>
                          <span className="admin-field-label">Questions</span>
                          {(sec.items || []).map((qa, j) => (
                            <div className="admin-faq-row" key={j}>
                              <input
                                placeholder="Question"
                                value={qa.q || ''}
                                onChange={(e) =>
                                  setSection(i, {
                                    items: sec.items.map((x, xi) =>
                                      xi === j ? { ...x, q: e.target.value } : x
                                    ),
                                  })
                                }
                              />
                              <textarea
                                placeholder="Answer"
                                rows={2}
                                value={qa.a || ''}
                                onChange={(e) =>
                                  setSection(i, {
                                    items: sec.items.map((x, xi) =>
                                      xi === j ? { ...x, a: e.target.value } : x
                                    ),
                                  })
                                }
                              />
                              <button
                                type="button"
                                className="admin-btn admin-btn-sm"
                                onClick={() => setSection(i, { items: sec.items.filter((_, xi) => xi !== j) })}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="admin-btn admin-btn-sm"
                            onClick={() =>
                              setSection(i, { items: [...(sec.items || []), { q: '', a: '' }] })
                            }
                          >
                            + Add question
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  <div className="admin-add-section">
                    <span className="admin-field-label">Add section:</span>
                    {SECTION_TYPES.map(([type, label]) => (
                      <button
                        key={type}
                        type="button"
                        className="admin-btn admin-btn-sm"
                        onClick={() => addSection(type)}
                      >
                        + {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="admin-form-actions">
              <button type="submit" className="admin-btn admin-btn-primary">
                Save
              </button>
              <button type="button" className="admin-btn" onClick={close}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {loading ? (
        <Loader inline status="Loading services" />
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Slug</th>
              <th>Name</th>
              <th>Icon</th>
              <th>Order</th>
              <th>Visible</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id}>
                <td>{s.id}</td>
                <td>{s.slug}</td>
                <td>{s.name}</td>
                <td>{s.icon || '—'}</td>
                <td>{s.display_order}</td>
                <td>
                  <Toggle checked={s.is_active} onChange={() => toggle(s.id)} />
                </td>
                <td className="admin-actions">
                  <button className="admin-btn admin-btn-sm" onClick={() => startEdit(s)}>
                    Edit
                  </button>
                  <button
                    className="admin-btn admin-btn-sm admin-btn-danger"
                    onClick={() => remove(s.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
